import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import {
  PageContainer,
  PageHeader,
  Button,
  Card,
  CardHeader,
  CardBody,
  Alert,
  LoadingState,
  FormGroup,
  Label,
  Input,
  useToast,
} from '../components/ui';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-xl);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionText = styled.p`
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin: 0 0 var(--space-md);
`;

const WebhookBox = styled.div`
  margin-top: var(--space-xl);
  padding: var(--space-md);
  background: var(--info-soft);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-md);

  h3 {
    font-size: var(--font-size-base);
    margin: 0 0 var(--space-sm);
    color: var(--text-primary);
  }

  p {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    margin: 0 0 var(--space-sm);
    line-height: 1.5;
  }

  ul {
    margin: var(--space-sm) 0 0 var(--space-lg);
    padding: 0;
  }
`;

interface ConfigItem {
  id?: number;
  chiave: string;
  valore: string;
  descrizione?: string;
  tenant_id?: number;
}

const ImpostazioniIntegrazioniPage: React.FC = () => {
  const { token } = useAuth();
  const toast = useToast();
  const [, setConfigs] = useState<ConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Valori locali del form
  const [stripePubKey, setStripePubKey] = useState('');
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [nexiTerminalId, setNexiTerminalId] = useState('');
  const [nexiMacKey, setNexiMacKey] = useState('');

  useEffect(() => {
    fetchConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caricamento una tantum al mount
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL || '/api'}/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data: ConfigItem[] = res.data;
      setConfigs(data);

      // Pre-popoliamo i campi con le configurazioni conosciute
      setStripePubKey(data.find(c => c.chiave === 'STRIPE_PUBLIC_KEY')?.valore || '');
      setStripeSecretKey(data.find(c => c.chiave === 'STRIPE_SECRET_KEY')?.valore || '');
      setNexiTerminalId(data.find(c => c.chiave === 'NEXI_TERMINAL_ID')?.valore || '');
      setNexiMacKey(data.find(c => c.chiave === 'NEXI_MAC_KEY')?.valore || '');

    } catch (err: any) {
      console.error('Errore caricamento configurazioni:', err);
      setError('Impossibile caricare le configurazioni');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async (chiave: string, valore: string, descrizione: string) => {
    await axios.put(`${process.env.REACT_APP_API_URL || '/api'}/config/${chiave}`, {
      valore,
      descrizione
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      setError(null);
      // Salviamo tutte e 4 le chiavi
      await saveConfig('STRIPE_PUBLIC_KEY', stripePubKey, 'Chiave Pubblica Stripe per terminale cliente');
      await saveConfig('STRIPE_SECRET_KEY', stripeSecretKey, 'Chiave Privata Stripe (RESTRICTED o SECRET)');
      await saveConfig('NEXI_TERMINAL_ID', nexiTerminalId, "Terminal ID per l'integrazione POS Nexi N700");
      await saveConfig('NEXI_MAC_KEY', nexiMacKey, 'Chiave MAC fornita da Nexi per cifrare le richieste');
      toast.success('Configurazioni salvate.');
      fetchConfigs();
    } catch (err: any) {
      console.error('Errore durante il salvataggio:', err);
      setError('Errore di salvataggio delle configurazioni');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState label="Caricamento impostazioni..." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Integrazioni POS e Pagamenti"
        actions={
          <Button onClick={handleSaveAll} disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva Tutte Le Chiavi'}
          </Button>
        }
      />

      {error && <Alert $variant="error">{error}</Alert>}

      <Grid>
        <Card>
          <CardHeader>
            <h3>Configurazione Stripe (QR Code)</h3>
          </CardHeader>
          <CardBody>
            <SectionText>
              Queste chiavi abilitano i pagamenti in Self-Service via smartphone scansionando il QR.
            </SectionText>
            <FormGroup>
              <Label>Stripe Public Key</Label>
              <Input
                type="text"
                value={stripePubKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStripePubKey(e.target.value)}
                placeholder="pk_test_..."
              />
            </FormGroup>
            <FormGroup>
              <Label>Stripe Secret Key</Label>
              <Input
                type="password"
                value={stripeSecretKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStripeSecretKey(e.target.value)}
                placeholder="sk_test_... oppure rk_test_..."
              />
            </FormGroup>

            <WebhookBox>
              <h3>Impostazione Webhook Stripe</h3>
              <p>
                Il webhook per Stripe è già implementato all'interno del codice all'endpoint <strong>/webhooks/stripe</strong>.
                Sulla dashboard di Stripe, configura un nuovo webhook con questo URL:
                <br /><code>https://api.iltuodominio.com/webhooks/stripe</code>
              </p>
              <p>
                <strong>Eventi da Selezionare:</strong>
              </p>
              <ul>
                <li><code>checkout.session.completed</code> (Essenziale per gli ordini Self-Service)</li>
                <li><code>invoice.payment_succeeded</code></li>
                <li><code>invoice.payment_failed</code></li>
                <li><code>customer.subscription.deleted</code></li>
              </ul>
              <Alert $variant="warning">
                <strong>Chiave Segreta del Webhook:</strong> dopo aver creato il webhook, copia la chiave segreta (inizia con <code>whsec_...</code>) e incollala all'interno del file <strong>.env</strong> del server o nelle variabili ambiente sotto il nome <code>STRIPE_WEBHOOK_SECRET</code>. Senza questa chiave, il server non accetterà le conferme di pagamento.
              </Alert>
            </WebhookBox>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h3>Configurazione Nexi (POS N700)</h3>
          </CardHeader>
          <CardBody>
            <SectionText>
              Configurazioni fornite dalla cassa Nexi per avviare transazioni sul POS locale in cloud.
            </SectionText>
            <FormGroup>
              <Label>Nexi Terminal ID (TID)</Label>
              <Input
                type="text"
                value={nexiTerminalId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNexiTerminalId(e.target.value)}
                placeholder="Es. 12054..."
              />
            </FormGroup>
            <FormGroup>
              <Label>Nexi MAC Signature Key</Label>
              <Input
                type="password"
                value={nexiMacKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNexiMacKey(e.target.value)}
                placeholder="Inserisci la chiave MAC"
              />
            </FormGroup>
          </CardBody>
        </Card>
      </Grid>
    </PageContainer>
  );
};

export default ImpostazioniIntegrazioniPage;
